/**
 * Storefront API Cart - Uses session cart token (cart.js) with Storefront API.
 * Same cart for both Ajax API and Storefront API - no sync needed.
 * Requires theme.storefrontApi.token in Theme Settings > Cart.
 */

(function () {
  const LINES_CACHE_KEY = "storefront_cart_lines";
  const CART_JS_KEY = "storefront_session_token";

  let lineCache = null;
  let sessionTokenCache = null;

  function getLineCache() {
    if (lineCache === null) {
      try {
        const stored = localStorage.getItem(LINES_CACHE_KEY);
        lineCache = stored ? JSON.parse(stored) : {};
      } catch {
        lineCache = {};
      }
    }
    return lineCache;
  }

  function saveLineCache() {
    try {
      localStorage.setItem(LINES_CACHE_KEY, JSON.stringify(lineCache || {}));
    } catch {}
  }

  function updateLineCacheFromCart(cart) {
    const cache = getLineCache();
    const lines = cart?.lines?.edges || [];
    const variantIdsInCart = new Set();
    lines.forEach(({ node }) => {
      const variantId = node.merchandise?.id?.split("/").pop();
      if (variantId) {
        variantIdsInCart.add(variantId);
        cache[variantId] = { lineId: node.id, quantity: node.quantity || 0 };
      }
    });
    // Remove stale entries for variants no longer in cart (e.g. after removal)
    Object.keys(cache).forEach((k) => {
      if (!variantIdsInCart.has(k)) delete cache[k];
    });
    saveLineCache();
  }

  function getConfig() {
    const config = typeof theme !== "undefined" && theme.storefrontApi;
    const hasToken = config?.token && String(config.token).trim().length > 0;
    const hasEndpoint = config?.endpoint && String(config.endpoint).trim().length > 0;
    if (!config || !hasToken || !hasEndpoint) return null;
    return config;
  }

  function getCartJsUrl() {
    const cartBase =
      (typeof theme !== "undefined" && theme.routes?.cart) || "/cart";
    return cartBase.endsWith(".js") ? cartBase : `${cartBase}.js`;
  }

  /**
   * Returns true if token has ?key= (real cart token). Empty cart tokens lack the key.
   */
  function hasValidToken(token) {
    return token && typeof token === "string" && token.includes("?key=");
  }

  /**
   * Reads cart token from cookie (no fetch). Same value as cart.js token.
   * Returns null if cookie not readable (HttpOnly) or not set.
   */
  function getTokenFromCookie() {
    try {
      const cookies = document.cookie.split("; ");
      for (let i = 0; i < cookies.length; i++) {
        const eq = cookies[i].indexOf("=");
        if (eq === -1) continue;
        const name = cookies[i].slice(0, eq).trim();
        if (name === "cart") {
          let value = cookies[i].slice(eq + 1).trim();
          if (value && value.length > 0) {
            try {
              value = decodeURIComponent(value);
            } catch {}
            return value;
          }
          return null;
        }
      }
    } catch {}
    return null;
  }

  /**
   * Fetches session cart from cart.js and returns the token.
   * Uses same cart as Ajax API - changes apply to cart.js automatically.
   */
  async function fetchSessionCartToken() {
    const url = getCartJsUrl();
    const res = await fetch(url, { credentials: "same-origin" });
    const data = await res.json();
    const token = data?.token;
    if (token) {
      sessionTokenCache = token;
      try {
        localStorage.setItem(CART_JS_KEY, token);
      } catch {}
    }
    return token || null;
  }

  /**
   * Returns Storefront API cart ID from session token.
   * gid://shopify/Cart/{token} - same cart as cart.js
   * Prefers: cookie (no fetch) → memory cache → localStorage → fetch cart.js
   */
  async function getSessionCartId() {
    if (sessionTokenCache) {
      return `gid://shopify/Cart/${sessionTokenCache}`;
    }
    const fromCookie = getTokenFromCookie();
    if (fromCookie) {
      sessionTokenCache = fromCookie;
      return `gid://shopify/Cart/${fromCookie}`;
    }
    try {
      const stored = localStorage.getItem(CART_JS_KEY);
      if (stored) {
        sessionTokenCache = stored;
        return `gid://shopify/Cart/${stored}`;
      }
    } catch {}
    const token = await fetchSessionCartToken();
    if (!token) return null;
    return `gid://shopify/Cart/${token}`;
  }

  function clearSessionCartCache() {
    sessionTokenCache = null;
    try {
      localStorage.removeItem(CART_JS_KEY);
      localStorage.removeItem(LINES_CACHE_KEY);
    } catch {}
    lineCache = {};
  }

  function updateCartLinks() {
    const cartPageUrl =
      (typeof theme !== "undefined" && theme.routes?.cart) || "/cart";
    const cartIcon = document.getElementById("cart-icon");
    if (cartIcon) cartIcon.setAttribute("href", cartPageUrl);
    document.querySelectorAll('a[href*="/cart"]').forEach((a) => {
      if (a.href.includes("/cart/c/")) {
        a.setAttribute("href", cartPageUrl);
      }
    });
  }

  function toGid(variantId) {
    return `gid://shopify/ProductVariant/${variantId}`;
  }

  /**
   * Adds first item via cart/add.js when cart is empty (no valid token with key).
   * Returns cart in Storefront format for consistency with addOrUpdateCart.
   */
  async function ajaxAddToCart(variantId, quantity) {
    const cartAddBase =
      (typeof theme !== "undefined" && theme.routes?.cartAdd) ||
      getCartJsUrl().replace(".js", "/add");
    const url = cartAddBase.endsWith(".js") ? cartAddBase : `${cartAddBase}.js`;
    const formData = new FormData();
    formData.set("id", variantId);
    formData.set("quantity", quantity || 1);

    const res = await fetch(url, {
      method: "POST",
      credentials: "same-origin",
      body: formData,
    });
    const data = await res.json();

    if (!res.ok) {
      throw new Error(data.description || data.message || "Failed to add to cart");
    }

    const items = data.items || [];
    const token = getTokenFromCookie() || data.token;
    if (token) {
      sessionTokenCache = token;
      try {
        localStorage.setItem(CART_JS_KEY, token);
      } catch {}
    }

    const cart = {
      lines: {
        edges: items.map((item) => ({
          node: {
            id: `gid://shopify/CartLine/ajax-${item.key || item.variant_id}`,
            quantity: item.quantity || 0,
            merchandise: { id: toGid(item.variant_id) },
          },
        })),
      },
      checkoutUrl: token
        ? `${typeof window !== "undefined" ? window.location.origin : ""}/cart/c/${token}`
        : null,
    };
    // Don't cache line IDs from ajax - they're not Storefront format. Token is cached above.
    return cart;
  }

  function graphqlFetch(query, variables = {}) {
    const config = getConfig();
    if (!config) return Promise.reject(new Error("Storefront API not configured"));

    return fetch(config.endpoint, {
      method: "POST",
      credentials: "same-origin",
      headers: {
        "Content-Type": "application/json",
        "X-Shopify-Storefront-Access-Token": config.token,
      },
      body: JSON.stringify({ query, variables }),
    }).then((res) => res.json());
  }

  // Write queue: mutations (add/update/remove) must run strictly in order to avoid race conditions.
  let writeQueue = Promise.resolve();

  function enqueueCartWrite(fn) {
    const next = writeQueue.then(
      () => fn(),
      () => fn()
    );
    writeQueue = next;
    return next;
  }

  // Reads wait for pending writes to drain, then run. Multiple reads can run in parallel.
  function runCartRead(fn) {
    return writeQueue.then(() => fn(), () => fn());
  }

  // Coalesce identical read requests (same key) to avoid redundant fetches
  const readCoalesceMap = new Map();

  function coalescedRead(key, fn) {
    const existing = readCoalesceMap.get(key);
    if (existing) return existing;
    const promise = runCartRead(fn).finally(() => readCoalesceMap.delete(key));
    readCoalesceMap.set(key, promise);
    return promise;
  }

  function isConflictError(result) {
    const errs = result?.errors || [];
    return errs.some(
      (e) => e?.extensions?.code === "CONFLICT" || /conflict/i.test(e?.message || "")
    );
  }

  function graphqlFetchWithRetry(query, variables, retries = 2) {
    return enqueueCartWrite(async () => {
      let lastResult;
      for (let i = 0; i <= retries; i++) {
        lastResult = await graphqlFetch(query, variables);
        if (!isConflictError(lastResult)) return lastResult;
        if (i < retries) {
          await new Promise((r) => setTimeout(r, 300 * (i + 1)));
        }
      }
      return lastResult;
    });
  }

  async function cartLinesAdd(cartId, variantId, quantity) {
    const mutation = `
      mutation cartLinesAdd($cartId: ID!, $lines: [CartLineInput!]!) {
        cartLinesAdd(cartId: $cartId, lines: $lines) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const result = await graphqlFetchWithRetry(mutation, {
      cartId,
      lines: [
        {
          merchandiseId: toGid(variantId),
          quantity: quantity || 1,
        },
      ],
    });

    const { cart, userErrors } = result.data?.cartLinesAdd || {};
    if (userErrors?.length) {
      throw new Error(userErrors.map((e) => e.message).join(", "));
    }
    if (!cart) {
      throw new Error(result.errors?.[0]?.message || "Failed to add to cart");
    }

    // Mutation only returns minimal cart — do a full paginated fetch for lines
    const fullCart = await getCart(cart.id);
    return fullCart ?? cart;
  }


 async function cartLinesUpdate(cartId, lineId, quantity) {
  const mutation = `
    mutation cartLinesUpdate($cartId: ID!, $lines: [CartLineUpdateInput!]!) {
      cartLinesUpdate(cartId: $cartId, lines: $lines) {
        cart {
          id
          checkoutUrl
        }
        userErrors {
          field
          message
        }
      }
    }
  `;

  const result = await graphqlFetchWithRetry(mutation, {
    cartId,
    lines: [{ id: lineId, quantity }],
  });

  const { cart, userErrors } = result.data?.cartLinesUpdate || {};
  if (userErrors?.length) {
    throw new Error(userErrors.map((e) => e.message).join(", "));
  }
  if (!cart) {
    throw new Error(result.errors?.[0]?.message || "Failed to update cart");
  }

  // Mutation only returns minimal cart — do a full paginated fetch for lines
  const fullCart = await getCart(cart.id);
  return fullCart ?? cart;
}


  async function cartLinesRemove(cartId, lineIds) {
    const mutation = `
      mutation cartLinesRemove($cartId: ID!, $lineIds: [ID!]!) {
        cartLinesRemove(cartId: $cartId, lineIds: $lineIds) {
          cart {
            id
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    const result = await graphqlFetchWithRetry(mutation, {
      cartId,
      lineIds: Array.isArray(lineIds) ? lineIds : [lineIds],
    });

    const { cart, userErrors } = result.data?.cartLinesRemove || {};
    if (userErrors?.length) {
      throw new Error(userErrors.map((e) => e.message).join(", "));
    }
    if (!cart) {
      throw new Error(result.errors?.[0]?.message || "Failed to remove from cart");
    }

    // Mutation only returns minimal cart — do a full paginated fetch for lines
    const fullCart = await getCart(cart.id);
    return fullCart ?? cart;
  }


  function isCartNotFoundError(err) {
    const msg = err?.message || String(err);
    return /cart does not exist|cart not found|INVALID/i.test(msg);
  }

  /**
   * Add or update item in cart. Uses session cart token - changes apply to cart.js.
   * When cart is empty (no valid token with key), first add uses cart/add.js.
   * After that, Storefront API with token from cookie (no fetch).
   * @param {number} variantId - Variant ID.
   * @param {number} quantity - Quantity to set (or add when assumeNewAdd).
   * @param {{ assumeNewAdd?: boolean }} [options] - assumeNewAdd: skip getCart when no cache (product-form add).
   */
  async function addOrUpdateCart(variantId, quantity, options = {}) {
    const perfStart = performance.now();
    const config = getConfig();
    if (!config) return null;

    // Try cookie first (no fetch), fallback to cart.js
    let freshToken = getTokenFromCookie();
    if (!freshToken) {
      freshToken = await fetchSessionCartToken();
    }

    const variantIdStr = String(variantId);
    const cached = getLineCache()[variantIdStr];

    // Empty cart: token has no ?key=, not valid for Storefront API. Use cart/add.js for first add.
    // (When we have cached but no valid token, cache is stale - treat as first add)
    if (!hasValidToken(freshToken)) {
      if (cached) {
        delete getLineCache()[variantIdStr];
        saveLineCache();
      }
      const cart = await ajaxAddToCart(variantId, quantity || 1);
      const durationMs = Math.round(performance.now() - perfStart);
      console.log(`[CartPerf] addToCart variantId=${variantId} quantity=${quantity || 1} operation=ajax-first-add durationMs=${durationMs}`);
      return { cart, oldQuantity: 0 };
    }

    if (!freshToken) return null;

    const hadToken = sessionTokenCache;
    const tokenChanged = hadToken && freshToken !== hadToken;
    sessionTokenCache = freshToken;
    try {
      localStorage.setItem(CART_JS_KEY, freshToken);
    } catch {}

    if (tokenChanged) {
      lineCache = {};
      try {
        localStorage.removeItem(LINES_CACHE_KEY);
      } catch {}
    }

    const cartId = `gid://shopify/Cart/${freshToken}`;

    // When we have no cached line: assumeNewAdd skips getCart (product-form add = 1 less round trip).
    // Otherwise fetch minimal cart for line IDs (update/remove from cart-items).
    let lineCacheForMutation = cached;
    if (!lineCacheForMutation && !(options.assumeNewAdd && quantity > 0)) {
      const cart = await getCartLinesForCache(cartId);
      if (cart) {
        lineCacheForMutation = getLineCache()[variantIdStr];
      }
    }

    const runMutation = async (retryCartId = cartId) => {
      const cache = lineCacheForMutation || getLineCache()[variantIdStr];
      if (cache) {
        if (quantity <= 0) {
          return cartLinesRemove(retryCartId, cache.lineId);
        }
        return cartLinesUpdate(retryCartId, cache.lineId, quantity);
      }
      return cartLinesAdd(retryCartId, variantId, quantity || 1);
    };

    try {
      let cart = await runMutation();
      updateLineCacheFromCart(cart);
      const op = cached ? (quantity <= 0 ? "remove" : "update") : "add";
      const durationMs = Math.round(performance.now() - perfStart);
      console.log(`[CartPerf] addToCart variantId=${variantId} quantity=${quantity ?? (cached ? cached.quantity : 1)} operation=${op} durationMs=${durationMs}`);
      return { cart, oldQuantity: cached?.quantity || 0 };
    } catch (e) {
      if (isCartNotFoundError(e)) {
        clearSessionCartCache();
        const retryToken = await fetchSessionCartToken();
        if (retryToken && hasValidToken(retryToken)) {
          sessionTokenCache = retryToken;
          const retryCartId = `gid://shopify/Cart/${retryToken}`;
          const cart = await runMutation(retryCartId);
          updateLineCacheFromCart(cart);
          const durationMs = Math.round(performance.now() - perfStart);
          console.log(`[CartPerf] addToCart variantId=${variantId} retry after cart-not-found durationMs=${durationMs}`);
          return { cart, oldQuantity: cached?.quantity || 0 };
        }
      }
      if (cached) {
        delete getLineCache()[variantIdStr];
        saveLineCache();
      }
      throw e;
    }
  }

  /**
   * Minimal cart query for cache population only (faster - smaller payload).
   * Use when we only need line IDs for update/remove.
   */
  async function getCartLinesForCache(cartId) {
    const query = `
      query getCartLines($cartId: ID!, $cursor: String) {
        cart(id: $cartId) {
          lines(first: 250, after: $cursor) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
    `;

    async function fetchAllLines(cursor = null, accumulatedEdges = []) {
      const result = await coalescedRead(`lines:${cartId}:${cursor ?? 'start'}`, () =>
        graphqlFetch(query, { cartId, cursor })
      );

      const cart = result.data?.cart || null;
      if (!cart) return null;

      const { edges, pageInfo } = cart.lines;
      const allEdges = [...accumulatedEdges, ...edges];

      if (pageInfo.hasNextPage) {
        return fetchAllLines(pageInfo.endCursor, allEdges);
      }

      // Return a cart-shaped object with all lines merged
      return {
        ...cart,
        lines: { edges: allEdges }
      };
    }

    const cart = await fetchAllLines();
    if (cart) updateLineCacheFromCart(cart);
    return cart;
  }


  /**
   * Fetch cart by ID (session cart ID from cart.js token).
   * Full cart for UI (drawer, etc). Uses queue for consistency.
   */
  async function getCart(cartId) {
    if (!cartId) {
      cartId = await getSessionCartId();
      if (!cartId) return null;
    }

    const query = `
      query getCart($cartId: ID!, $cursor: String) {
        cart(id: $cartId) {
          id
          lines(first: 250, after: $cursor) {
            edges {
              node {
                id
                quantity
                merchandise {
                  ... on ProductVariant {
                    id
                    title
                    price {
                      amount
                      currencyCode
                    }
                    product {
                      title
                      handle
                    }
                    image {
                      url
                      altText
                    }
                  }
                }
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
          checkoutUrl
        }
      }
    `;

    async function fetchAllLines(cursor = null, accumulatedEdges = []) {
      const result = await coalescedRead(`cart:${cartId}:${cursor ?? 'start'}`, () =>
        graphqlFetch(query, { cartId, cursor })
      );

      const cart = result.data?.cart || null;
      if (!cart) return null;

      const { edges, pageInfo } = cart.lines;
      const allEdges = [...accumulatedEdges, ...edges];

      if (pageInfo.hasNextPage) {
        return fetchAllLines(pageInfo.endCursor, allEdges);
      }

      return {
        ...cart,
        lines: { edges: allEdges }
      };
    }

    const cart = await fetchAllLines();
    if (cart) updateLineCacheFromCart(cart);
    return cart;
  }


  function isStorefrontApiEnabled() {
    return !!getConfig();
  }

  function updateCartIconBubble(lineCount) {
    const cartIconEl = document.getElementById("cart-icon-bubble");
    if (!cartIconEl) return;
    if (lineCount > 0) {
      cartIconEl.innerHTML = `
        <div class="header__cart-count absolute bg-theme-text text-theme-bg text-center">
          ${lineCount < 10000 ? `<span aria-hidden="true" class="header__cart-count-number">${lineCount}</span>` : ""}
          <span class="visually-hidden">${lineCount === 1 ? "1 item in cart" : `${lineCount} items in cart`}</span>
        </div>
      `;
    } else {
      cartIconEl.innerHTML = "";
    }
  }

  window.StorefrontCart = {
    addOrUpdateCart,
    getCart,
    getSessionCartId,
    clearSessionCartCache,
    updateCartLinks,
    updateCartIconBubble,
    isStorefrontApiEnabled,
  };

  function init() {
    if (!getConfig()) return;
    updateCartLinks();
    getSessionCartId()
      .then((cartId) => {
        if (!cartId) return;
        return getCart(cartId);
      })
      .then((cart) => {
        if (cart) {
          const lineCount = cart.lines?.edges?.length || 0;
          updateCartIconBubble(lineCount);
        }
      })
      .catch(() => {});
  }

  if (typeof document !== "undefined" && document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();